// "Your turn": the list of things a chat asks of the person. An item is a
// question (choice, confirm, text, form, ui), a request (check, operate,
// provide) or a draft message the person sends to someone (draft). This file
// holds the shapes both ends validate with and the pure helpers around them.
// Text written for the AI lives in turn-text.ts.
import { z } from "zod";

/** Question kinds (the person decides). */
export const TURN_QUESTION_KINDS = [
  "choice",
  "confirm",
  "text",
  "form",
  "ui",
] as const;
/** Request kinds (work only the person can do). */
export const TURN_REQUEST_KINDS = ["check", "operate", "provide"] as const;
/** A draft message the person copies and sends. */
export const TURN_DRAFT_KIND = "draft" as const;
export const TURN_KINDS = [
  ...TURN_QUESTION_KINDS,
  ...TURN_REQUEST_KINDS,
  TURN_DRAFT_KIND,
] as const;
export type TurnKind = (typeof TURN_KINDS)[number];
export type TurnRequestKind = (typeof TURN_REQUEST_KINDS)[number];

export function isTurnRequestKind(kind: string): kind is TurnRequestKind {
  return (TURN_REQUEST_KINDS as readonly string[]).includes(kind);
}

export function isTurnDraftKind(kind: string): kind is typeof TURN_DRAFT_KIND {
  return kind === TURN_DRAFT_KIND;
}

/** Most open items a chat may hold (adding more is refused with `turn_full`). */
export const TURN_OPEN_MAX = 20;
/** Size limit of the UI code of a kind ui item. */
export const TURN_UI_MAX = 20000;
/** Size limit of a draft message body (about one Slack post). */
export const TURN_DRAFT_TEXT_MAX = 4000;
export const TURN_DRAFT_SUBJECT_MAX = 200;
export const TURN_DRAFT_REPLY_FROM_MAX = 200;
export const TURN_LINKS_MAX = 8;
/**
 * The key of what the owner is writing to answer an item (a person's draft,
 * shared by their devices); one of its fields adds `:<part>`. The drafts go
 * when the item closes.
 */
export const turnDraftKey = (itemId: string, part?: string) =>
  part ? `turn:${itemId}:${part}` : `turn:${itemId}`;
/** Files attachable to one answer. */
export const TURN_FILES_MAX = 10;
/** Size limit of answer text (same as a post). */
export const TURN_TEXT_MAX = 20000;
/** How many closed items `turn.list` returns (newest last). */
export const TURN_LIST_CLOSED_MAX = 10;

export const TURN_DRAFT_TARGETS = ["slack", "email"] as const;
export type TurnDraftTarget = (typeof TURN_DRAFT_TARGETS)[number];

/**
 * The two sides of a translated draft: text is what is sent, source is the
 * confirmation in the person's language. `stale` names the side that still
 * holds the previous content after the person edited the other one.
 */
export const TURN_DRAFT_SIDES = ["text", "source"] as const;
export type TurnDraftSide = (typeof TURN_DRAFT_SIDES)[number];

const URL_MAX = 2000;
const SLACK_ID_MAX = 64;
const SLACK_NAME_MAX = 200;
const SINGLE_LINE = /^[^\r\n]*$/;
/** Short BCP 47 form (`en`, `zh-TW`). */
const DRAFT_LANG_RE = /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;

export function isHttpUrlText(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

const httpUrl = z
  .string()
  .max(URL_MAX)
  .refine(isHttpUrlText, { error: "must be an http(s) URL" });

/** Places to look at. `url` is an http(s) URL; the others are ids. */
export const TURN_LINK_KINDS = [
  "page",
  "chat",
  "issue",
  "message",
  "case",
  "url",
] as const;
export type TurnLinkKind = (typeof TURN_LINK_KINDS)[number];
export const TurnLinkInput = z.object({
  kind: z.enum(TURN_LINK_KINDS),
  target_id: z.string().min(1).max(URL_MAX),
  label: z.string().max(200).optional(),
});
export type TurnLinkInput = z.infer<typeof TurnLinkInput>;

/** The original message a draft replies to (copied verbatim; not format-checked). */
export const TurnDraftReplyTo = z.object({
  text: z.string().min(1).max(TURN_DRAFT_TEXT_MAX),
  from: z.string().min(1).max(TURN_DRAFT_REPLY_FROM_MAX).optional(),
  url: httpUrl.optional(),
  /** Translation into the person's language when the original is in another one. */
  translation: z.string().min(1).max(TURN_DRAFT_TEXT_MAX).optional(),
});
export type TurnDraftReplyTo = z.infer<typeof TurnDraftReplyTo>;

/** Where a Slack draft goes. The person sees it; only the agent sets it. */
export const TurnDraftSlackTo = z.object({
  channel_id: z.string().min(1).max(SLACK_ID_MAX),
  channel_name: z.string().min(1).max(SLACK_NAME_MAX).optional(),
  thread_ts: z.string().min(1).max(SLACK_ID_MAX).optional(),
  url: httpUrl.optional(),
});
export type TurnDraftSlackTo = z.infer<typeof TurnDraftSlackTo>;

/**
 * The content of one item. Kept a plain object so the ask tool can spread its
 * shape into the MCP input. The kind/field combination is checked by
 * `turnSpecProblem`.
 */
export const TurnSpec = z.object({
  kind: z.enum(TURN_KINDS),
  title: z.string().min(1).max(200),
  body: z.string().max(4000).optional(),
  options: z
    .array(
      z.object({
        id: z.string().min(1).max(64),
        label: z.string().min(1).max(200),
        description: z.string().max(500).optional(),
        recommended: z.boolean().optional(),
      }),
    )
    .max(4)
    .optional(),
  multi: z.boolean().optional(),
  fields: z
    .array(
      z.object({
        id: z.string().min(1).max(64),
        label: z.string().min(1).max(200),
        type: z.enum(["text", "number", "date"]),
        placeholder: z.string().max(200).optional(),
      }),
    )
    .max(8)
    .optional(),
  links: z.array(TurnLinkInput).max(TURN_LINKS_MAX).optional(),
  /** The related Case. Omitted in a Case's chat means that Case. */
  case_id: z.string().min(1).max(100).optional(),
  /** kind ui: the UI code (the inside of a ui fence). */
  ui: z.string().max(TURN_UI_MAX).optional(),
  // The rest is for drafts only.
  target: z.enum(TURN_DRAFT_TARGETS).optional(),
  text: z.string().min(1).max(TURN_DRAFT_TEXT_MAX).optional(),
  subject: z
    .string()
    .max(TURN_DRAFT_SUBJECT_MAX)
    .regex(SINGLE_LINE, { error: "subject must be one line" })
    .optional(),
  lang: z
    .string()
    .max(16)
    .regex(DRAFT_LANG_RE, { error: "lang must be a language code like en" })
    .optional(),
  source: z.string().min(1).max(TURN_DRAFT_TEXT_MAX).optional(),
  /** Set and cleared only by the core; dropped from what the agent sends. */
  stale: z.enum(TURN_DRAFT_SIDES).optional(),
  reply_to: TurnDraftReplyTo.optional(),
  slack_to: TurnDraftSlackTo.optional(),
});
export type TurnSpec = z.infer<typeof TurnSpec>;

/** check: ok / fix, operate: done / failed, draft: sent / unsent. */
export const TURN_RESULTS = [
  "ok",
  "fix",
  "done",
  "failed",
  "sent",
  "unsent",
] as const;
export type TurnResult = (typeof TURN_RESULTS)[number];
export const TURN_RESULTS_OF: Record<
  "check" | "operate",
  readonly TurnResult[]
> = { check: ["ok", "fix"], operate: ["done", "failed"] };
export const TURN_DRAFT_RESULTS = [
  "sent",
  "unsent",
] as const satisfies readonly TurnResult[];

/** Kinds whose answer may carry files (those with an input field). */
export const TURN_FILE_KINDS: readonly TurnKind[] = [
  "choice",
  "confirm",
  "text",
  "check",
  "operate",
  "provide",
  "form",
];

/**
 * What the person (or a parent agent) answers. `text` is Markdown from the
 * input field; `fileIds` are files attached from it.
 */
export const TurnAnswer = z.object({
  selected: z.array(z.string().max(64)).optional(),
  text: z.string().max(TURN_TEXT_MAX).optional(),
  fields: z.record(z.string(), z.string().max(1000)).optional(),
  confirmed: z.boolean().optional(),
  result: z.enum(TURN_RESULTS).optional(),
  fileIds: z.array(z.string().min(1).max(100)).max(TURN_FILES_MAX).optional(),
});
export type TurnAnswer = z.infer<typeof TurnAnswer>;

/** Whether an answer says anything at all. */
export function turnAnswerIsEmpty(a: TurnAnswer): boolean {
  return !(
    (a.selected?.length ?? 0) > 0 ||
    (a.text ?? "").trim() !== "" ||
    Object.keys(a.fields ?? {}).length > 0 ||
    a.confirmed !== undefined ||
    a.result !== undefined ||
    (a.fileIds?.length ?? 0) > 0
  );
}

/**
 * Why an answer does not fit the item's kind, as a code and its parameters
 * (null when it fits). The core turns it into the person's language; the
 * agent uses `turnAnswerProblemText`.
 */
export type TurnAnswerProblem =
  | { code: "need_result"; kind: "check" | "operate"; results: string[] }
  | { code: "need_fix" }
  | { code: "need_failure_reason" }
  | { code: "need_file_or_text" }
  | { code: "draft_need_result"; results: string[] }
  | { code: "sent_no_text" }
  | { code: "draft_no_files" }
  | { code: "question_no_result" }
  | { code: "need_text" }
  | { code: "no_files"; kind: TurnKind };

export function turnAnswerProblem(
  spec: Pick<TurnSpec, "kind">,
  answer: TurnAnswer,
): TurnAnswerProblem | null {
  const text = (answer.text ?? "").trim();
  if (spec.kind === "check" || spec.kind === "operate") {
    const allowed = TURN_RESULTS_OF[spec.kind];
    if (!answer.result || !allowed.includes(answer.result))
      return { code: "need_result", kind: spec.kind, results: [...allowed] };
    if ((answer.result === "fix" || answer.result === "failed") && text === "")
      return answer.result === "fix"
        ? { code: "need_fix" }
        : { code: "need_failure_reason" };
    return null;
  }
  if (spec.kind === "provide") {
    if ((answer.fileIds?.length ?? 0) === 0 && text === "")
      return { code: "need_file_or_text" };
    return null;
  }
  if (isTurnDraftKind(spec.kind)) {
    if (
      !answer.result ||
      !(TURN_DRAFT_RESULTS as readonly string[]).includes(answer.result)
    )
      return { code: "draft_need_result", results: [...TURN_DRAFT_RESULTS] };
    // The sent text is in the item's spec; copying it would duplicate it.
    if (answer.result === "sent" && text !== "")
      return { code: "sent_no_text" };
    if ((answer.fileIds?.length ?? 0) > 0) return { code: "draft_no_files" };
    return null;
  }
  if (answer.result !== undefined) return { code: "question_no_result" };
  if (spec.kind === "ui" && text === "") return { code: "need_text" };
  if ((answer.fileIds?.length ?? 0) > 0 && !TURN_FILE_KINDS.includes(spec.kind))
    return { code: "no_files", kind: spec.kind };
  return null;
}

/**
 * The out-of-sync mark after the person edits side `which` of a translated
 * draft. With no mark the other side becomes stale; editing the marked side
 * clears it; a mark on the other side stays.
 */
export function draftStaleAfterHumanEdit(
  stale: TurnDraftSide | undefined,
  which: TurnDraftSide,
): TurnDraftSide | undefined {
  if (stale === which) return undefined;
  if (stale !== undefined) return stale;
  return which === "text" ? "source" : "text";
}

type DraftRevisionLike = { spec: TurnSpec; cause: string };

/**
 * Whether the person edited the draft (the last previous content is theirs).
 * After an agent rewrite the last one is no longer the person's.
 */
export function turnEditedByHuman(
  revisions: readonly { cause: string }[] | undefined,
): boolean {
  return revisions?.at(-1)?.cause === "human";
}

/**
 * The text the agent last produced: the trailing human revision's content if
 * the person edited, otherwise the current content.
 */
export function draftAgentSpec(
  spec: TurnSpec,
  revisions: readonly DraftRevisionLike[],
): TurnSpec {
  const last = revisions.at(-1);
  return last?.cause === "human" ? last.spec : spec;
}

function sameDraftFields(a: TurnSpec, b: TurnSpec): boolean {
  return (
    a.text === b.text &&
    a.source === b.source &&
    a.lang === b.lang &&
    a.subject === b.subject &&
    a.stale === b.stale
  );
}

/** Content after the person's edit and what to do with the revisions. */
export interface HumanDraftEdit {
  spec: TurnSpec;
  /**
   * push: add the agent's text as a human revision. keep: a repeated edit.
   * drop: back to exactly the agent's text, remove the trailing human revision.
   */
  revisions: "push" | "keep" | "drop";
}

/**
 * The draft after the person edits one side and the subject; null when
 * nothing changed. `sameText` compares Markdown loosely (the editor writes the
 * same text differently). Returning to the agent's text drops the human
 * revision so the agent is not told "the person edited it" for nothing. The
 * Slack destination is never touched by the person's edit.
 */
export function humanDraftEdit(
  current: { spec: TurnSpec; revisions: readonly DraftRevisionLike[] },
  edit: { which: TurnDraftSide; text: string; subject?: string },
  sameText: (a: string, b: string) => boolean,
): HumanDraftEdit | null {
  const { which, text } = edit;
  const before = current.spec;
  const agent = draftAgentSpec(before, current.revisions);
  const spec: TurnSpec = { ...before };
  const was = before[which];
  if (was === undefined || !sameText(was, text)) {
    const agentText = agent[which];
    spec[which] =
      agentText !== undefined && sameText(agentText, text) ? agentText : text;
    if (before.lang !== undefined) {
      const back = spec.text === agent.text && spec.source === agent.source;
      const stale = back
        ? undefined
        : draftStaleAfterHumanEdit(before.stale, which);
      if (stale === undefined) delete spec.stale;
      else spec.stale = stale;
    }
  }
  if (edit.subject !== undefined) {
    if (edit.subject.trim() === "") delete spec.subject;
    else spec.subject = edit.subject;
  }
  if (sameDraftFields(spec, before)) return null;
  const edited = turnEditedByHuman(current.revisions);
  if (edited && sameDraftFields(spec, agent))
    return {
      spec: withDraftSlackTo(agent, before.slack_to),
      revisions: "drop",
    };
  return { spec, revisions: edited ? "keep" : "push" };
}

/** The spec with `slack_to` replaced (removed when undefined). */
export function withDraftSlackTo(
  spec: TurnSpec,
  slackTo: TurnDraftSlackTo | undefined,
): TurnSpec {
  const { slack_to: _, ...rest } = spec;
  return slackTo === undefined ? rest : { ...rest, slack_to: slackTo };
}

/**
 * Whether only the Slack destination differs. Such a rewrite is written
 * without asking and without bumping the version.
 */
export function onlySlackToChanged(
  a: TurnSpec,
  b: TurnSpec,
  same: (x: TurnSpec, y: TurnSpec) => boolean,
): boolean {
  return (
    !same(a, b) &&
    same(withDraftSlackTo(a, undefined), withDraftSlackTo(b, undefined))
  );
}

/** Drops the out-of-sync mark from content the agent produced. */
export function withoutDraftStale(spec: TurnSpec): TurnSpec {
  if (spec.stale === undefined) return spec;
  const { stale: _, ...rest } = spec;
  return rest;
}

/** Where a new item goes: front, back, or by the linked Case's due date. */
export const TURN_POSITIONS = ["front", "back", "auto"] as const;
export type TurnPosition = (typeof TURN_POSITIONS)[number];

/** What prompted an agent's rewrite of an item. */
export const TURN_EDIT_CAUSES = ["conversation", "external"] as const;
export type TurnEditCause = (typeof TURN_EDIT_CAUSES)[number];

/** How a rewrite was handled. */
export const TURN_EDIT_OUTCOMES = [
  "silent",
  "marked",
  "proposed",
  "unchanged",
  "cleared",
] as const;
export type TurnEditOutcome = (typeof TURN_EDIT_OUTCOMES)[number];

/** Input of the ask tool: the spec without the core-only mark, plus where. */
export const TurnAskInput = TurnSpec.omit({ stale: true }).extend({
  position: z.enum(TURN_POSITIONS).optional(),
});
export type TurnAskInput = z.infer<typeof TurnAskInput>;

/** Input of turn_edit: the whole spec after the rewrite, the item, why, and the version read. */
export const TurnEditInput = TurnSpec.omit({ stale: true }).extend({
  id: z.string().min(1).max(100),
  reason: z.string().min(1).max(500),
  cause: z.enum(TURN_EDIT_CAUSES),
  version: z.number().int().min(0).optional(),
});
export type TurnEditInput = z.infer<typeof TurnEditInput>;

/** Input of turn_answer_child: the item and an answer without files. */
export const TurnAnswerChildInput = TurnAnswer.omit({ fileIds: true }).extend({
  id: z.string().min(1).max(100),
});
export type TurnAnswerChildInput = z.infer<typeof TurnAnswerChildInput>;

export const SLACK_DRAFT_ERROR_MAX = 500;

/** The agent's report of placing a Slack draft. */
export const SlackDraftResult = z.object({
  state: z.enum(["placed", "failed"]),
  url: httpUrl.nullable().optional(),
  error: z.string().min(1).max(SLACK_DRAFT_ERROR_MAX).nullable().optional(),
  /** The request time read from the item; a newer request refuses an old result. */
  requestedAt: z.string().max(100).optional(),
});
export type SlackDraftResult = z.infer<typeof SlackDraftResult>;

/** Stored state of an item. */
export const TURN_STATES = ["open", "done", "withdrawn"] as const;
export type TurnState = (typeof TURN_STATES)[number];

/** State as shown: queued, shown (first, not yet seen), seen, done, withdrawn. */
export const TURN_STATUSES = [
  "queued",
  "shown",
  "seen",
  "done",
  "withdrawn",
] as const;
export type TurnStatus = (typeof TURN_STATUSES)[number];

/** human: answered in the list. agent: marked done from a post. parent: a parent agent answered. */
export const TURN_CLOSED_BY = ["human", "agent", "parent"] as const;
export type TurnClosedBy = (typeof TURN_CLOSED_BY)[number];

/** Whether a closed item leaves a line in the chat (withdrawn only after it was seen). */
export function turnItemLeavesLine(item: {
  state: TurnState;
  seenAt: string | null;
}): boolean {
  if (item.state === "done") return true;
  if (item.state === "withdrawn") return item.seenAt !== null;
  return false;
}
